import React, { useState, useEffect, useRef, useCallback } from "react";
import { Check, ChevronDown, ChevronUp, Download, X } from "lucide-react";
import { toast } from "react-toastify";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";

import "../../style/lowStock.css";
import SearchIcon from "../../assets/search-icon.svg";
import CalendarIcon from "../../assets/calender-icon.svg";
import FilterIcon from "../../assets/icons/FilterIcon";
import NoDataIcon from "../../assets/icons/NoDataIcon";
import CriticalStockImg from "../../assets/critical-stock-img.svg";
import LowStockImg from "../../assets/low-stock-img.svg";
import TotalProductsImg from "../../assets/total-products-img.svg";
import FilePdf from "../../assets/file-pdf.svg";
import FileExcel from "../../assets/microsoft-excel-logo.svg";
import FileCsv from "../../assets/file-csv.svg";
import IgnoreStock from "./components/IgnoreStock";
import {
  getLowStockSummary,
  getLowStockProducts,
  ignoreProduct,
} from "../../services/low-stock-api";
import { useSelector } from "react-redux";

// Module-level request cache to prevent duplicate API calls in React StrictMode
let pendingSummaryRequest = null;
let summaryRequestCompleted = false;
let pendingProductsRequest = null;
let productsRequestCompleted = false;

function LowStock() {
  const { isPartsURL, isAccessoriesURL, isGodownURL } = useSelector((state) => state.domain);

  // State management
  const [products, setProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [sortField, setSortField] = useState("");
  const [sortDirection, setSortDirection] = useState("asc");
  const [dateFilter, setDateFilter] = useState("this_month");
  const [showDateFilterDropdown, setShowDateFilterDropdown] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [showStatusFilterDropdown, setShowStatusFilterDropdown] =
    useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showIgnoreModal, setShowIgnoreModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [summaryData, setSummaryData] = useState({
    critical_stock: 0,
    low_stock: 0,
    total_products: 0,
  });
  const dateFilterDropdownRef = useRef(null);
  const statusFilterDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);
  const hasFetchedInitialSummary = useRef(false);
  const hasFetchedInitialProducts = useRef(false);

  // Fetch summary data
  const fetchSummary = useCallback(async (isInitialLoad = false) => {
    // For initial load, use request deduplication
    if (isInitialLoad) {
      if (summaryRequestCompleted) {
        return;
      }
      if (pendingSummaryRequest) {
        try {
          await pendingSummaryRequest;
        } catch {
          // Ignore errors from pending request
        }
        return;
      }

      // Create and store the request
      pendingSummaryRequest = (async () => {
        try {
          const data = await getLowStockSummary();
          setSummaryData({
            critical_stock: data.critical_stock || 0,
            low_stock: data.low_stock || 0,
            total_products: data.total_products || 0,
          });
          summaryRequestCompleted = true;
        } catch (error) {
          console.error("Error fetching summary:", error);
          toast.error("Failed to load summary data");
          throw error;
        } finally {
          pendingSummaryRequest = null;
        }
      })();

      await pendingSummaryRequest;
      return;
    }

    // For non-initial loads, proceed normally
    try {
      const data = await getLowStockSummary();
      setSummaryData({
        critical_stock: data.critical_stock || 0,
        low_stock: data.low_stock || 0,
        total_products: data.total_products || 0,
      });
    } catch (error) {
      console.error("Error fetching summary:", error);
      toast.error("Failed to load summary data");
    }
  }, []);

  // Fetch products with filters
  const fetchProducts = useCallback(
    async (isInitialLoad = false) => {
      // For initial load, use request deduplication
      if (isInitialLoad) {
        if (productsRequestCompleted) {
          return;
        }
        if (pendingProductsRequest) {
          try {
            await pendingProductsRequest;
          } catch {
            // Ignore errors from pending request
          }
          return;
        }

        // Create and store the request
        pendingProductsRequest = (async () => {
          setLoading(true);
          try {
            // Build ordering parameter
            let ordering = "";
            if (sortField) {
              const fieldMap = {
                product_name: "name",
                sku: "sku",
                category: "category",
                current_stock: "current_stock",
                min_stock: "min_stock",
              };
              const apiField = fieldMap[sortField] || sortField;
              ordering = sortDirection === "desc" ? `-${apiField}` : apiField;
            }

            const params = {
              page: currentPage,
              page_size: itemsPerPage,
              search: searchTerm || undefined,
              date_filter: dateFilter || undefined,
              status: statusFilter !== "all" ? statusFilter : undefined,
              ordering: ordering || undefined,
            };

            const data = await getLowStockProducts(params);
            setProducts(data.results || []);
            setTotalCount(data.count || 0);
            productsRequestCompleted = true;
          } catch (error) {
            console.error("Error fetching products:", error);
            toast.error("Failed to load low stock products");
            setProducts([]);
            setTotalCount(0);
            throw error;
          } finally {
            setLoading(false);
            pendingProductsRequest = null;
          }
        })();

        await pendingProductsRequest;
        return;
      }

      // For non-initial loads, proceed normally
      setLoading(true);
      try {
        // Build ordering parameter
        let ordering = "";
        if (sortField) {
          const fieldMap = {
            product_name: "name",
            sku: "sku",
            category: "category",
            current_stock: "current_stock",
            min_stock: "min_stock",
          };
          const apiField = fieldMap[sortField] || sortField;
          ordering = sortDirection === "desc" ? `-${apiField}` : apiField;
        }

        const params = {
          page: currentPage,
          page_size: itemsPerPage,
          search: searchTerm || undefined,
          date_filter: dateFilter || undefined,
          status: statusFilter !== "all" ? statusFilter : undefined,
          ordering: ordering || undefined,
        };

        const data = await getLowStockProducts(params);
        setProducts(data.results || []);
        setTotalCount(data.count || 0);
      } catch (error) {
        console.error("Error fetching products:", error);
        toast.error("Failed to load low stock products");
        setProducts([]);
        setTotalCount(0);
      } finally {
        setLoading(false);
      }
    },
    [
      currentPage,
      itemsPerPage,
      searchTerm,
      dateFilter,
      statusFilter,
      sortField,
      sortDirection,
    ]
  );

  // Initial load - fetch summary
  useEffect(() => {
    // Prevent duplicate API calls in React StrictMode
    if (hasFetchedInitialSummary.current) {
      return;
    }
    hasFetchedInitialSummary.current = true;

    // Pass isInitialLoad=true to enable request deduplication
    fetchSummary(true);

    // Reset flags on unmount
    return () => {
      summaryRequestCompleted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Initial load - fetch products (only once on mount)
  useEffect(() => {
    // Prevent duplicate API calls in React StrictMode
    if (hasFetchedInitialProducts.current) {
      return;
    }
    hasFetchedInitialProducts.current = true;

    // Pass isInitialLoad=true to enable request deduplication
    fetchProducts(true);

    // Reset flags on unmount
    return () => {
      productsRequestCompleted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch products when filters change (after initial load)
  useEffect(() => {
    // Skip if initial load hasn't completed yet
    if (!hasFetchedInitialProducts.current || !productsRequestCompleted) {
      return;
    }

    // For filter changes, proceed normally (not initial load)
    fetchProducts(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentPage,
    itemsPerPage,
    searchTerm,
    dateFilter,
    statusFilter,
    sortField,
    sortDirection,
  ]);

  // Handle click outside dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dateFilterDropdownRef.current &&
        !dateFilterDropdownRef.current.contains(event.target)
      ) {
        setShowDateFilterDropdown(false);
      }
      if (
        statusFilterDropdownRef.current &&
        !statusFilterDropdownRef.current.contains(event.target)
      ) {
        setShowStatusFilterDropdown(false);
      }
      if (
        exportDropdownRef.current &&
        !exportDropdownRef.current.contains(event.target)
      ) {
        setShowExportDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Handle search with debounce - reset to page 1 when search changes
  useEffect(() => {
    const delayedSearch = setTimeout(() => {
      setCurrentPage(1);
    }, 500);

    return () => clearTimeout(delayedSearch);
  }, [searchTerm]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  // Format numbers for display
  const formatNumber = (num) => {
    return new Intl.NumberFormat("en-US").format(num || 0);
  };

  // Pagination
  const totalPages = Math.ceil(totalCount / itemsPerPage);
  const shouldShowPagination = totalCount > 25;

  const SortableHeader = ({ field, children }) => (
    <th className="sortable-header" onClick={() => handleSort(field)}>
      {children}
      {sortField === field && (
        <span className="ms-1">{sortDirection === "asc" ? "↑" : "↓"}</span>
      )}
    </th>
  );

  // Truncate text for display with ellipsis
  const truncateText = (text, maxLength = 20) => {
    if (text === null || text === undefined) return "-";
    const textString = String(text);
    if (textString.trim() === "") return "-";
    if (textString.length <= maxLength) return textString;
    return `${textString.substring(0, maxLength)}...`;
  };

  const dateFilterOptions = [
    { value: "today", label: "Today" },
    { value: "yesterday", label: "Yesterday" },
    { value: "last_7_days", label: "Last 7 Days" },
    { value: "this_month", label: "This Month" },
  ];

  const statusFilterOptions = [
    { value: "all", label: "All Statuses" },
    { value: "out of stock", label: "Out of Stock" },
    { value: "low", label: "Low Stock" },
    { value: "in stock", label: "In Stock" },
  ];

  const exportOptions = [
    { value: "pdf", label: "PDF", icon: FilePdf },
    { value: "xlsx", label: "XLSX", icon: FileExcel },
    { value: "csv", label: "CSV", icon: FileCsv },
  ];

  const getDateFilterLabel = () => {
    const option = dateFilterOptions.find((opt) => opt.value === dateFilter);
    return option ? option.label : "This Month";
  };

  const getStatusFilterLabel = () => {
    const option = statusFilterOptions.find(
      (opt) => opt.value === statusFilter
    );
    return option ? option.label : "All Statuses";
  };

  const getExportLabel = () => {
    return "Export";
  };

  const handleDateFilterSelect = (value) => {
    setDateFilter(value);
    setShowDateFilterDropdown(false);
    setCurrentPage(1);
  };

  const handleStatusFilterSelect = (value) => {
    setStatusFilter(value);
    setShowStatusFilterDropdown(false);
    setCurrentPage(1);
  };

  // Export to PDF
  const exportToPDF = () => {
    if (products.length === 0) {
      toast.error("No data to export");
      return;
    }

    const doc = new jsPDF();

    // Add title
    doc.setFontSize(16);
    doc.text("Low Stock Report", 14, 22);

    // Add generation date
    doc.setFontSize(10);
    doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 14, 30);

    // Prepare table data
    const tableData = products.map((product) => [
      product.product_name || "-",
      product.brand_name || "-",
      product.category_name || "-",
      product.stock_left || 0,
      product.status
        ? product.status.charAt(0).toUpperCase() + product.status.slice(1)
        : "-",
      product.last_restock || "-",
    ]);

    // Create table using autoTable
    autoTable(doc, {
      head: [
        [
          "Product Name",
          "Brand",
          "Category",
          "Stock Left",
          "Status",
          "Last Restock",
        ],
      ],
      body: tableData,
      startY: 38,
      styles: {
        fontSize: 8,
        cellPadding: 3,
      },
      headStyles: {
        fillColor: [246, 248, 250],
        textColor: [87, 96, 106],
        fontStyle: "bold",
      },
      alternateRowStyles: {
        fillColor: [248, 249, 250],
      },
    });

    // Save the PDF
    doc.save(`low-stock-report-${new Date().toISOString().split("T")[0]}.pdf`);
    toast.success("PDF exported successfully");
  };

  // Export to Excel
  const exportToExcel = () => {
    if (products.length === 0) {
      toast.error("No data to export");
      return;
    }

    // Prepare data for Excel
    const excelData = products.map((product) => ({
      "Product Name": product.product_name || "-",
      Brand: product.brand_name || "-",
      Category: product.category_name || "-",
      "Stock Left": product.stock_left || 0,
      Status: product.status
        ? product.status.charAt(0).toUpperCase() + product.status.slice(1)
        : "-",
      "Last Restock": product.last_restock || "-",
    }));

    // Create workbook and worksheet
    const ws = XLSX.utils.json_to_sheet(excelData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Low Stock");

    // Set column widths
    ws["!cols"] = [
      { wch: 30 }, // Product Name
      { wch: 15 }, // Brand
      { wch: 20 }, // Category
      { wch: 15 }, // Stock Left
      { wch: 15 }, // Status
      { wch: 15 }, // Last Restock
    ];

    // Save the Excel file
    XLSX.writeFile(
      wb,
      `low-stock-report-${new Date().toISOString().split("T")[0]}.xlsx`
    );
    toast.success("Excel file exported successfully");
  };

  // Export to CSV
  const exportToCSV = () => {
    if (products.length === 0) {
      toast.error("No data to export");
      return;
    }

    // Prepare CSV data
    const csvHeaders = [
      "Product Name",
      "Brand",
      "Category",
      "Stock Left",
      "Status",
      "Last Restock",
    ];
    const csvData = products.map((product) => [
      product.product_name || "-",
      product.brand_name || "-",
      product.category_name || "-",
      product.stock_left || 0,
      product.status
        ? product.status.charAt(0).toUpperCase() + product.status.slice(1)
        : "-",
      product.last_restock || "-",
    ]);

    // Combine headers and data
    const csvContent = [csvHeaders, ...csvData]
      .map((row) => row.map((field) => `"${field}"`).join(","))
      .join("\n");

    // Create and download CSV file
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `low-stock-report-${new Date().toISOString().split("T")[0]}.csv`
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("CSV file exported successfully");
  };

  const handleExportSelect = (value) => {
    setShowExportDropdown(false);

    switch (value) {
      case "pdf":
        exportToPDF();
        break;
      case "xlsx":
        exportToExcel();
        break;
      case "csv":
        exportToCSV();
        break;
      default:
        console.log(`Export format ${value} not supported`);
    }
  };

  // Handle Ignore Modal
  const handleOpenIgnoreModal = (product) => {
    setSelectedProduct(product);
    setShowIgnoreModal(true);
  };

  const handleCloseIgnoreModal = () => {
    setShowIgnoreModal(false);
    setSelectedProduct(null);
  };

  const handleConfirmIgnore = async (product) => {
    try {
      await ignoreProduct(product.id);
      toast.success(
        `${product.product_name} has been ignored from low stock alerts`
      );
      // Refresh the data
      fetchProducts();
      fetchSummary();
    } catch (error) {
      toast.error("Failed to ignore product");
    }
  };

  return (
    <>
      {/* Content Area */}
      <div className="card rounded-3 p-4 border-0 mb-1 lowStock-container">
        <div className="lowStock-layout">
          {/* Header */}
          <div className="lowStock-top">
            <div className="lowStock-header">
              <div>
                <h2 className="lowStock-title">Low Stock</h2>
                <p className="lowStock-subtitle">
                  Manage and restock products running out of inventory
                </p>
              </div>
            </div>

            {/* Summary Cards */}
            <div className="row mb-2">
              <div className="col-md-3">
                <div
                  className="card brands-summary-card"
                  style={{
                    border: "1px solid #DCE3EB",
                    borderRadius: "10px",
                  }}
                >
                  <div className="card-body text-center d-flex align-items-start justify-content-between">
                    <div className="d-flex flex-column align-items-start justify-content-center mb-2 gap-2">
                      <h6
                        className="mb-0 text-muted"
                        style={{
                          fontSize: "14px",
                          color: "#6E7781",
                          fontWeight: "400",
                        }}
                      >
                        Out of Stock
                      </h6>
                      <h4
                        className="mb-0 fw-semibold"
                        style={{
                          fontSize: "22px",
                          color: "#424A53",
                          fontWeight: "500",
                        }}
                      >
                        {formatNumber(summaryData.critical_stock)} items
                      </h4>
                    </div>

                    <div className="d-flex align-items-center justify-content-center">
                      <img src={CriticalStockImg} alt="Out of Stock" />
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-md-3">
                <div
                  className="card brands-summary-card"
                  style={{
                    border: "1px solid #DCE3EB",
                    borderRadius: "10px",
                  }}
                >
                  <div className="card-body text-center d-flex align-items-start justify-content-between">
                    <div className="d-flex flex-column align-items-start justify-content-center mb-2 gap-2">
                      <h6
                        className="mb-0 text-muted"
                        style={{
                          fontSize: "14px",
                          color: "#6E7781",
                          fontWeight: "400",
                        }}
                      >
                        Low Stock
                      </h6>
                      <h4
                        className="mb-0 fw-semibold"
                        style={{
                          fontSize: "22px",
                          color: "#424A53",
                          fontWeight: "500",
                        }}
                      >
                        {formatNumber(summaryData.low_stock)} items
                      </h4>
                    </div>

                    <div className="d-flex align-items-center justify-content-center">
                      <img src={LowStockImg} alt="Low Stock" />
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-md-3">
                <div
                  className="card brands-summary-card"
                  style={{
                    border: "1px solid #DCE3EB",
                    borderRadius: "10px",
                  }}
                >
                  <div className="card-body text-center d-flex align-items-start justify-content-between">
                    <div className="d-flex flex-column align-items-start justify-content-center mb-2 gap-2">
                      <h6
                        className="mb-0 text-muted"
                        style={{
                          fontSize: "14px",
                          color: "#6E7781",
                          fontWeight: "400",
                        }}
                      >
                        Total Products
                      </h6>
                      <h4
                        className="mb-0 fw-semibold"
                        style={{
                          fontSize: "22px",
                          color: "#424A53",
                          fontWeight: "500",
                        }}
                      >
                        {formatNumber(summaryData.total_products)} items
                      </h4>
                    </div>

                    <div className="d-flex align-items-center justify-content-center">
                      <img src={TotalProductsImg} alt="Total Products" />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Search + Date Filter + Status Filter + Export */}
            <div className="search-filter-section mb-4">
              {/* Search Bar */}
              <div className="lowStock-search-container">
                <input
                  type="text"
                  className={`${isPartsURL ? "lowStock-search-input-parts" : isAccessoriesURL ? "lowStock-search-input-acc" : isGodownURL ? "lowStock-search-input-godown" : "lowStock-search-input"}`}
                  placeholder="Search by product name, SKU, category..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <img
                  src={SearchIcon}
                  alt="Search"
                  className="lowStock-search-icon"
                />
              </div>

              {/* Date Filter */}
              <div
                className="lowStock-filter-dropdown"
                ref={dateFilterDropdownRef}
              >
                <button
                  className={`${isPartsURL ? "lowStock-date-filter-btn-parts" : isAccessoriesURL ? "lowStock-date-filter-btn-acc" : isGodownURL ? "lowStock-date-filter-btn-godown" : "lowStock-date-filter-btn"}`}
                  onClick={() =>
                    setShowDateFilterDropdown(!showDateFilterDropdown)
                  }
                >
                  <img src={CalendarIcon} alt="Calendar" />
                  {getDateFilterLabel()}
                  {showDateFilterDropdown ? (
                    <ChevronUp
                      size={16}
                      className="lowStock-filter-dropdown-arrow"
                      style={{
                        width: "16px",
                        height: "16px",
                        color: "#424A53",
                      }}
                    />
                  ) : (
                    <ChevronDown
                      size={16}
                      className="lowStock-filter-dropdown-arrow"
                      style={{
                        width: "16px",
                        height: "16px",
                        color: "#424A53",
                      }}
                    />
                  )}
                </button>

                <div
                  className={`lowStock-filter-dropdown-content ${
                    showDateFilterDropdown ? "show" : ""
                  }`}
                >
                  {dateFilterOptions.map((option) => (
                    <div
                      key={option.value}
                      className={`lowStock-filter-dropdown-item ${
                        dateFilter === option.value ? "selected" : ""
                      }`}
                      onClick={() => handleDateFilterSelect(option.value)}
                    >
                      <Check
                        size={16}
                        className="lowStock-filter-dropdown-check"
                      />
                      {option.label}
                    </div>
                  ))}
                </div>
              </div>

              {/* All Status */}
              <div
                className="lowStock-filter-dropdown"
                ref={statusFilterDropdownRef}
              >
                <button
                  className={`${isPartsURL ? "lowStock-statuses-filter-btn-parts" : isAccessoriesURL ? "lowStock-statuses-filter-btn-acc" : isGodownURL ? "lowStock-statuses-filter-btn-godown" : "lowStock-statuses-filter-btn"}`}
                  onClick={() =>
                    setShowStatusFilterDropdown(!showStatusFilterDropdown)
                  }
                >
                  <FilterIcon size={16} />
                  {getStatusFilterLabel()}
                  {showStatusFilterDropdown ? (
                    <ChevronUp
                      size={16}
                      className="lowStock-filter-dropdown-arrow"
                      style={{
                        width: "16px",
                        height: "16px",
                        color: "#424A53",
                      }}
                    />
                  ) : (
                    <ChevronDown
                      size={16}
                      className="lowStock-filter-dropdown-arrow"
                      style={{
                        width: "16px",
                        height: "16px",
                        color: "#424A53",
                      }}
                    />
                  )}
                </button>

                <div
                  className={`lowStock-filter-dropdown-content ${
                    showStatusFilterDropdown ? "show" : ""
                  }`}
                >
                  {statusFilterOptions.map((option) => (
                    <div
                      key={option.value}
                      className={`lowStock-filter-dropdown-item ${
                        statusFilter === option.value ? "selected" : ""
                      }`}
                      onClick={() => handleStatusFilterSelect(option.value)}
                    >
                      <Check
                        size={16}
                        className="lowStock-filter-dropdown-check"
                      />
                      {option.label}
                    </div>
                  ))}
                </div>
              </div>

              {/* Export */}
              <div className="lowStock-filter-dropdown" ref={exportDropdownRef}>
                <button
                  className={`${isPartsURL ? "lowStock-date-filter-btn-parts" : isAccessoriesURL ? "lowStock-date-filter-btn-acc" : isGodownURL ? "lowStock-date-filter-btn-godown" : "lowStock-date-filter-btn"}`}
                  onClick={() => setShowExportDropdown(!showExportDropdown)}
                >
                  <Download size={16} />
                  {getExportLabel()}
                  {showExportDropdown ? (
                    <ChevronUp
                      size={16}
                      className="lowStock-filter-dropdown-arrow"
                      style={{
                        width: "16px",
                        height: "16px",
                        color: "#424A53",
                      }}
                    />
                  ) : (
                    <ChevronDown
                      size={16}
                      className="lowStock-filter-dropdown-arrow"
                      style={{
                        width: "16px",
                        height: "16px",
                        color: "#424A53",
                      }}
                    />
                  )}
                </button>

                <div
                  className={`lowStock-filter-dropdown-content ${
                    showExportDropdown ? "show" : ""
                  }`}
                >
                  {exportOptions.map((option) => (
                    <div
                      key={option.value}
                      className="lowStock-filter-dropdown-item"
                      onClick={() => handleExportSelect(option.value)}
                    >
                      <img
                        src={option.icon}
                        alt={option.label}
                        width="16"
                        height="16"
                      />
                      {option.label}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Products Table */}
          <div className="lowStock-table-wrapper">
            <div className="lowStock-table-responsive">
              <table className="lowStock-table">
                <thead>
                  <tr>
                    <SortableHeader field="product_name">
                      PRODUCT NAME
                    </SortableHeader>
                    <SortableHeader field="sku">BRAND</SortableHeader>
                    <SortableHeader field="category">CATEGORY</SortableHeader>
                    <SortableHeader field="current_stock">
                      STOCK LEFT
                    </SortableHeader>
                    <SortableHeader field="min_stock">STATUS</SortableHeader>
                    <th>LAST RESTOCK</th>
                    <th style={{ textAlign: "center" }}>ACTION</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="7" className="no-products-message">
                        <div
                          className=""
                          style={{
                            marginBottom: "4rem",
                            marginTop: "4rem",
                          }}
                        >
                          <div className="spinner-border" role="status">
                            <span className="visually-hidden">Loading...</span>
                          </div>
                          <h3 style={{ marginTop: "2rem" }}>
                            Loading Low Stock products...
                          </h3>
                        </div>
                      </td>
                    </tr>
                  ) : products.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="no-products-message">
                        <div
                          className=""
                          style={{
                            marginBottom: "4rem",
                            marginTop: "4rem",
                          }}
                        >
                          <NoDataIcon />
                          <h3 style={{ marginTop: "2rem" }}>
                            No Low Stock products found
                          </h3>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    products.map((product) => (
                      <tr key={product.id}>
                        <td className="lowStock-product-name">
                          {product.display_name ? (
                            <>
                              <span className="product-name-bold">
                                {product.display_name.split("|")[0].trim()}
                              </span>
                              <br />
                              <span className="product-details-small">
                                {product.display_name
                                  .split("|")
                                  .slice(1)
                                  .map((part) => part.trim())
                                  .join(" | ")}
                              </span>
                            </>
                          ) : (
                            truncateText(product.product_name, 30)
                          )}
                        </td>
                        <td>{product.brand_name || "-"}</td>
                        <td>{product.category_name || "-"}</td>
                        <td>
                          <span
                            style={{
                              color:
                                product.status === "out of stock"
                                  ? "#FF4564"
                                  : product.status === "low"
                                  ? "#FF9500"
                                  : "#34C759",
                              fontWeight: "600",
                            }}
                          >
                            ●{" "}
                          </span>
                          {product.stock_left || 0}
                        </td>
                        <td>
                          <span
                            className="lowStock-status-badge"
                            style={{
                              backgroundColor:
                                product.status === "out of stock"
                                  ? "rgba(255, 69, 100, 0.08)"
                                  : product.status === "low"
                                  ? "rgba(255, 149, 0, 0.08)"
                                  : "rgba(52, 199, 89, 0.08)",
                              color:
                                product.status === "out of stock"
                                  ? "#FF4564"
                                  : product.status === "low"
                                  ? "#FF9500"
                                  : "#34C759",
                            }}
                          >
                            {product.status.charAt(0).toUpperCase() +
                              product.status.slice(1)}
                          </span>
                        </td>
                        <td>{product.last_restock || "-"}</td>
                        <td>
                          <div className="lowStock-action-buttons">
                            <button
                              className="lowStock-action-btn"
                              title="Ignore"
                              onClick={() => handleOpenIgnoreModal(product)}
                            >
                              <X size={20} />
                              Ignore
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          {shouldShowPagination && (
            <div className="lowStock-pagination-sticky">
              <div className="lowStock-pagination-container">
                <div className="lowStock-pagination-info">
                  Page {currentPage} of {totalPages}
                </div>
                <div className="lowStock-pagination-buttons">
                  <button
                    className="previous"
                    onClick={() => {
                      const newPage = currentPage - 1;
                      setCurrentPage(newPage);
                    }}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </button>
                  <button
                    className={`${isPartsURL ? "next-lowStock-pagination-btn-parts" : isAccessoriesURL ? "next-lowStock-pagination-btn-acc" : isGodownURL ? "next-lowStock-pagination-btn-godown" : "next-lowStock-pagination-btn"}`}
                    onClick={() => {
                      const newPage = currentPage + 1;
                      setCurrentPage(newPage);
                    }}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Ignore Stock Modal */}
      <IgnoreStock
        isOpen={showIgnoreModal}
        onClose={handleCloseIgnoreModal}
        onConfirm={handleConfirmIgnore}
        product={selectedProduct}
      />
    </>
  );
}

export default LowStock;
